import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Network, Users, UserPlus, CalendarCheck, IndianRupee, AlertTriangle, Reply, Send } from "lucide-react";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, Panel, PageLoader, StatusBadge, ConfigRequired, EmptyState, Stat, brandName, Btn, TextInput } from "@/lib/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const LEAD_ST = ["new", "contacted", "qualified", "converted", "closed"];
const BOOKING_ST = ["pending", "confirmed", "cancelled", "completed"];

function ManageDialog({ row, tabKey, onClose, onSaved }) {
  const api = useApi();
  const opts = tabKey === "bookings" ? BOOKING_ST : LEAD_ST;
  const [status, setStatus] = useState(row?.status || opts[0]);
  const [notes, setNotes] = useState(row?.notes || "");
  const save = useMutation({
    mutationFn: () => api.patch(`/ecosystem/${tabKey}/${row.id}`, { status, notes }),
    onSuccess: (d) => { toast.success("Updated on the website"); onSaved?.(d); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-md" data-testid="eco-manage-dialog">
        <DialogHeader><DialogTitle>Manage {row?.name || row?.customer_name || "record"}</DialogTitle></DialogHeader>
        <label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Status</label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="eco-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{opts.map((s) => <SelectItem key={s} value={s} data-testid={`eco-status-${s}`}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea rows={3} placeholder="Internal notes…" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="eco-notes" />
        <Btn onClick={() => save.mutate()} disabled={save.isPending} data-testid="eco-manage-save">{save.isPending ? "Saving…" : "Save to website"}</Btn>
      </DialogContent>
    </Dialog>
  );
}

function ReplyDialog({ row, onClose }) {
  const api = useApi();
  const [subject, setSubject] = useState("Re: your enquiry with Gopi Gadhvi Group");
  const [message, setMessage] = useState(`Hi ${(row?.name || "there").split(" ")[0]},\n\nThank you for reaching out to the Gopi Gadhvi Group. `);
  const send = useMutation({
    mutationFn: () => api.post("/ecosystem/reply", { to: row.email, name: row.name, subject, message }),
    onSuccess: () => { toast.success(`Email sent to ${row.email}`); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-lg" data-testid="eco-reply-dialog">
        <DialogHeader><DialogTitle>Reply to {row?.name || row?.email}</DialogTitle></DialogHeader>
        <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="eco-reply-subject" />
        <Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="eco-reply-message" />
        <Btn onClick={() => send.mutate()} disabled={send.isPending || !message.trim()} data-testid="eco-reply-send">
          <Send className="h-4 w-4" /> {send.isPending ? "Sending…" : "Send reply"}
        </Btn>
      </DialogContent>
    </Dialog>
  );
}

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtMoney = (v, c = "INR") => (v == null ? "—" : `${c === "INR" ? "₹" : ""}${Number(v).toLocaleString("en-IN")}`);

const TABS = {
  leads: {
    label: "Leads", icon: UserPlus,
    cols: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "source", label: "Source" },
      { key: "status", label: "Status", badge: true },
      { key: "created_at", label: "Created", fmt: fmtDate },
    ],
  },
  customers: {
    label: "Customers", icon: Users,
    cols: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "lifetime_value", label: "Lifetime Value", fmt: (v) => fmtMoney(v) },
      { key: "created_at", label: "Since", fmt: fmtDate },
    ],
  },
  bookings: {
    label: "Bookings", icon: CalendarCheck,
    cols: [
      { key: "customer_name", label: "Customer" },
      { key: "service", label: "Service" },
      { key: "amount", label: "Amount", fmt: (v, r) => fmtMoney(v, r.currency) },
      { key: "status", label: "Status", badge: true },
      { key: "scheduled_at", label: "Scheduled", fmt: fmtDate },
    ],
  },
  payments: {
    label: "Payments", icon: IndianRupee,
    cols: [
      { key: "customer_name", label: "Customer" },
      { key: "amount", label: "Amount", fmt: (v, r) => fmtMoney(v, r.currency) },
      { key: "method", label: "Method" },
      { key: "status", label: "Status", badge: true },
      { key: "created_at", label: "Date", fmt: fmtDate },
    ],
  },
};

const toneFor = (status = "") => {
  const s = String(status).toUpperCase();
  if (["PAID", "CONFIRMED", "WON", "COMPLETED", "ACTIVE"].some((x) => s.includes(x))) return "ok";
  if (["PENDING", "NEW", "HOLD", "CREATED"].some((x) => s.includes(x))) return "warn";
  if (["FAILED", "CANCELLED", "LOST", "REFUNDED"].some((x) => s.includes(x))) return "err";
  return "muted";
};

function DataTable({ tabKey }) {
  const api = useApi();
  const { brand } = useBrand();
  const cfg = TABS[tabKey];
  const [replyRow, setReplyRow] = useState(null);
  const [manageRow, setManageRow] = useState(null);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["eco", tabKey, brand],
    queryFn: () => api.get(`/ecosystem/${tabKey}?brand_id=${brand}&limit=100`),
  });

  if (isLoading) return <PageLoader />;
  if (isError) {
    return (
      <Panel className="p-8">
        <EmptyState icon={AlertTriangle} title="Could not load from GGG backend" hint={error?.message} />
      </Panel>
    );
  }
  const items = data?.items || [];
  if (!items.length) {
    return (
      <Panel className="p-8">
        <EmptyState icon={cfg.icon} title={`No ${cfg.label.toLowerCase()} yet`} hint={`Nothing returned for ${brandName(brand)}.`} />
      </Panel>
    );
  }
  return (
    <Panel className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`eco-table-${tabKey}`}>
          <thead>
            <tr className="border-b border-border text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
              {cfg.cols.map((c) => <th key={c.key} className="px-4 py-3 font-medium whitespace-nowrap">{c.label}</th>)}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((row, i) => (
              <tr key={row.id || i} className="border-b border-border/50 hover:bg-accent/40 transition-colors" data-testid={`eco-row-${tabKey}-${i}`}>
                {cfg.cols.map((c) => {
                  const raw = row[c.key];
                  const val = c.fmt ? c.fmt(raw, row) : (raw ?? "—");
                  return (
                    <td key={c.key} className="px-4 py-3 whitespace-nowrap">
                      {c.badge ? <StatusBadge tone={toneFor(raw)}>{raw || "—"}</StatusBadge> : <span className="text-foreground/90">{val}</span>}
                    </td>
                  );
                })}
                <td className="px-4 py-3 whitespace-nowrap text-right space-x-1">
                  {(tabKey === "leads" || tabKey === "bookings") ? (
                    <Btn variant="ghost" size="sm" onClick={() => setManageRow(row)} data-testid={`eco-manage-${tabKey}-${i}`}>Manage</Btn>
                  ) : null}
                  {row.email ? (
                    <Btn variant="ghost" size="sm" onClick={() => setReplyRow(row)} data-testid={`eco-reply-${tabKey}-${i}`}>
                      <Reply className="h-3.5 w-3.5" /> Reply
                    </Btn>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {replyRow && <ReplyDialog row={replyRow} onClose={() => setReplyRow(null)} />}
      {manageRow && <ManageDialog row={manageRow} tabKey={tabKey} onClose={() => setManageRow(null)} />}
    </Panel>
  );
}

function Overview() {
  const api = useApi();
  const { brand } = useBrand();
  const { data, isLoading } = useQuery({
    queryKey: ["eco", "summary", brand],
    queryFn: () => api.get(`/ecosystem/summary?brand_id=${brand}`),
  });
  if (isLoading) return <PageLoader />;
  const s = data || {};
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="eco-summary">
      <Stat label="Leads / Enquiries" value={s.leads_total ?? "—"} />
      <Stat label="Customers" value={s.customers_total ?? "—"} />
      <Stat label="Bookings / Orders" value={s.bookings_total ?? "—"} />
      <Stat label="Revenue" value={s.revenue_total != null ? fmtMoney(s.revenue_total, s.currency) : "—"} tone="ok" />
    </div>
  );
}

export default function Ecosystem() {
  const api = useApi();
  const { brand } = useBrand();
  const [tab, setTab] = useState("overview");
  const { data: status, isLoading } = useQuery({ queryKey: ["eco-status"], queryFn: () => api.get("/ecosystem/status") });

  if (isLoading) return <PageLoader />;

  return (
    <div data-testid="ecosystem-page">
      <PageHeader kicker={`${brandName(brand)} · Ecosystem`} title="GGG Live Data">
        {status?.configured
          ? <StatusBadge tone="ok" dot>Connected · Read-only</StatusBadge>
          : <ConfigRequired hint="Set GGG_API_BASE_URL / GGG_API_KEY" />}
      </PageHeader>

      {!status?.configured ? (
        <Panel className="p-8 max-w-2xl">
          <div className="flex items-start gap-3">
            <Network className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <div className="font-head text-lg font-semibold mb-2">Connect the existing GGG backend</div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This surfaces live Leads, Customers, Bookings and Payments from the existing Gopi Gadhvi
                Group website — <span className="text-foreground">read-only</span>, through secure authenticated
                API endpoints. The Command Centre never touches the GGG database directly.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                Add read-only endpoints in the GGG website project, then set{" "}
                <code className="text-primary font-mono text-xs">GGG_API_BASE_URL</code> and{" "}
                <code className="text-primary font-mono text-xs">GGG_API_KEY</code> in this backend's
                environment. Endpoint code is provided in the handoff notes.
              </p>
            </div>
          </div>
        </Panel>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList data-testid="eco-tabs">
            <TabsTrigger value="overview" data-testid="eco-tab-overview">Overview</TabsTrigger>
            {Object.entries(TABS).map(([k, v]) => (
              <TabsTrigger key={k} value={k} data-testid={`eco-tab-${k}`}>{v.label}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview" className="mt-4"><Overview /></TabsContent>
          {Object.keys(TABS).map((k) => (
            <TabsContent key={k} value={k} className="mt-4"><DataTable tabKey={k} /></TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
